import React, { useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
  InputAdornment, // ⬅️ added
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility"; // ⬅️ added
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff"; // ⬅️ added
import { motion } from "framer-motion";

import withAuthentication from "../../hoc/withAuthentication";
import Footer from "../../components/Footer/Footer";
import SignInForm from "./SignInForm";
import { SignInMainContainer } from "./SignInDesign";
import { StageLinkLogo } from "../../components/Branding/StageLinkLogo";
import { useNavigate } from "react-router-dom"; // ⬅️ added

const DEV_PASS = "only";

/** — Netflix-ish tokens (design only) — */
const COLORS = {
  bg: "#0b0b0b",
  panel: "#141414",
  border: "rgba(255,255,255,0.12)",
  text: "#ffffff",
  textMuted: "rgba(255,255,255,0.75)",
  red: "#E50914",
  redHover: "#b20710",
};

function SignIn() {
  /* ---------------- gate dialog state ---------------- */
  const [unlocked, setUnlocked] = useState(false);
  const [passValue, setPassValue] = useState("");
  const [err, setErr] = useState("");
  const [showDevPass, setShowDevPass] = useState(false); // ⬅️ added

  const verifyPassword = () => {
    if (passValue === DEV_PASS) setUnlocked(true);
    else setErr("Incorrect password");
  };

  /* ---------------- responsive helpers ---------------- */
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate(); // ⬅️ added

  /* ---------------- sign-in page content --------------- */
  const SignInLayout = (
    <>
      <SignInMainContainer
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          minHeight: "100vh",
          bgcolor: COLORS.bg,
          position: "relative",
          overflow: "hidden",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(1200px 600px at 10% 10%, rgba(229,9,20,0.12), transparent 60%), radial-gradient(900px 500px at 90% 20%, rgba(255,255,255,0.06), transparent 60%)",
            pointerEvents: "none",
          },
        }}
      >
        {!isMobile && (
          <Box
            flex={1}
            sx={{
              borderRadius: { xs: 0, md: "24px 0 0 24px" },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: { xs: 4, md: 8 },
              position: "relative",
              background:
                "linear-gradient(180deg, rgba(20,20,20,0.85), rgba(20,20,20,0.75))",
              borderRight: `1px solid ${COLORS.border}`,
            }}
          >
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <StageLinkLogo />

                <Typography
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                  sx={{
                    textAlign: "center",
                    lineHeight: 1.25,
                    fontWeight: 800,
                    fontFamily: "Lexend, sans-serif",
                    mt: 0.75,
                    fontSize: { xs: 12, sm: 13, md: 14, lg: 15, xl: 16 },
                    color: COLORS.text,
                  }}
                >
                  <Box component="span" sx={{ color: COLORS.red }}>
                    The Real-Time Infrastructure
                  </Box>
                  <br />
                  <Box component="span" sx={{ color: COLORS.textMuted }}>
                    For Global Audience Engagement.
                  </Box>
                </Typography>
              </Box>
            </motion.div>
          </Box>
        )}

        {/* Right pane = actual sign-in form */}
        <Box
          flex={1}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: { xs: 3, md: 8 },
            bgcolor: "transparent",
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            style={{ width: "100%", maxWidth: 560 }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              sx={{
                p: { xs: 2.5, sm: 3, md: 4 },
                borderRadius: 2,
                bgcolor: COLORS.panel,
                color: COLORS.text,
                border: `1px solid ${COLORS.border}`,
                boxShadow: "0 16px 48px rgba(0,0,0,0.5)",
              }}
            >
              <SignInForm />
            </Box>
          </motion.div>
        </Box>
      </SignInMainContainer>

      <Footer />
    </>
  );

  /* ---------------- render ---------------------------- */
  return (
    <>
      {/* Developer-password dialog – visible until unlocked */}
      {!unlocked && (
        <Dialog
          open
          fullWidth
          maxWidth="xs"
          onClose={() => navigate(-1)} // ⬅️ close/cancel navigates back
          PaperProps={{
            sx: {
              borderRadius: 2,
              bgcolor: COLORS.panel,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 16px 48px rgba(0,0,0,0.6)",
              backgroundImage:
                "linear-gradient(180deg, rgba(10,10,10,0.95), rgba(10,10,10,0.85))",
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontFamily: "Lexend",
              fontWeight: 800,
              letterSpacing: "-0.01em",
              color: COLORS.text,
              pb: 1,
            }}
          >
            Developer Access
            <IconButton
              size="small"
              onClick={() => navigate(-1)} // ⬅️ close icon = back
              sx={{ color: COLORS.textMuted }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ fontFamily: "Lexend" }}>
            <Typography variant="body2" mb={2} sx={{ color: COLORS.textMuted }}>
              This environment is restricted. Enter the developer password.
            </Typography>

            <TextField
              fullWidth
              autoFocus
              label="Password"
              type={showDevPass ? "text" : "password"} // ⬅️ visibility toggle
              value={passValue}
              error={!!err}
              helperText={err}
              onChange={(e) => {
                setErr("");
                setPassValue(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") verifyPassword();
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      onClick={() => setShowDevPass((s) => !s)}
                      sx={{ color: COLORS.textMuted }}
                      aria-label={
                        showDevPass ? "Hide password" : "Show password"
                      }
                    >
                      {showDevPass ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  bgcolor: COLORS.bg,
                  color: COLORS.text,
                  borderRadius: 1.5,
                  "& fieldset": { borderColor: COLORS.border },
                  "&:hover fieldset": { borderColor: COLORS.textMuted },
                  "&.Mui-focused fieldset": { borderColor: COLORS.red },
                },
                "& .MuiInputLabel-root": {
                  color: COLORS.textMuted,
                  "&.Mui-focused": { color: COLORS.text },
                },
                "& .MuiFormHelperText-root": {
                  color: "#ff6b6b",
                },
              }}
            />
          </DialogContent>

          <DialogActions sx={{ pr: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => navigate(-1)} // ⬅️ Cancel navigates to previous page
              sx={{
                color: COLORS.text,
                textTransform: "none",
                fontWeight: 700,
                borderColor: COLORS.border,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={verifyPassword}
              variant="contained"
              sx={{
                fontFamily: "Lexend",
                fontWeight: 800,
                px: 3,
                borderRadius: 1.5,
                bgcolor: COLORS.red,
                boxShadow: "0 10px 24px rgba(229,9,20,0.35)",
                textTransform: "none",
                "&:hover": { bgcolor: COLORS.redHover },
              }}
            >
              Enter
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Actual sign-in page, only after unlock */}
      {unlocked && SignInLayout}
    </>
  );
}

export default withAuthentication(SignIn, ["/sign-in"]);
